export type OwnerSettings = {
  maintenanceMode: {
    enabled: boolean
    message: string
  }
  debugSessionRetentionDays: number
  ownerConsoleLeaseMinutes: number
  ownerAlertEmail: string
  publicCatalogFreeze: boolean
  globalSignupBonusCr: number
  globalDailyNewSignupCrCap: number
  globalAiGenerationCurseDefaults: {
    enabled: boolean
    durationHours: number
    breakCostCr: number
  }
  economy: EconomySettings
}

export type SpinTierRange = {
  minCredits: number
  maxCredits: number
}

export type PlanMonthlyCredits = {
  ESSENTIAL_TIER: number
  PROFESSIONAL_TIER: number
  personal_basic: number
  personal_full: number
}

export type EconomySettings = {
  referredSignupBonus: number
  checklistRewardCredits: number
  checklistDailyLimit: number
  feedbackRewardCredits: number
  spinRanges: {
    lucky: SpinTierRange
    mega: SpinTierRange
    giga: SpinTierRange
  }
  spinsPerDay: number
  ticketExpiryDays: number
  milestoneRaidPool: number
  bossCurseBreakCost: number
  referralMaxPeople: number
  referralLadder: number[]
  referralPurchaseCredits: number
  referralPurchaseHoldDays: number
  flagHoldDays: number
  transferCapPerMonth: number
  transferMinMemberDays: number
  inrPerCreditFree: number
  inrPerCreditPaid: number
  usdInr: number
  planMonthlyCredits: PlanMonthlyCredits
}

export const DEFAULT_ECONOMY_SETTINGS: EconomySettings = {
  referredSignupBonus: 50,
  checklistRewardCredits: 2,
  checklistDailyLimit: 2,
  feedbackRewardCredits: 5,
  spinRanges: {
    lucky: { minCredits: 1, maxCredits: 3 },
    mega: { minCredits: 3, maxCredits: 10 },
    giga: { minCredits: 10, maxCredits: 30 },
  },
  spinsPerDay: 3,
  ticketExpiryDays: 14,
  milestoneRaidPool: 10,
  bossCurseBreakCost: 10,
  referralMaxPeople: 4,
  referralLadder: [20, 20, 30, 30],
  referralPurchaseCredits: 150,
  referralPurchaseHoldDays: 7,
  flagHoldDays: 7,
  transferCapPerMonth: 200,
  transferMinMemberDays: 14,
  inrPerCreditFree: 0.10,
  inrPerCreditPaid: 0.35,
  usdInr: 85,
  planMonthlyCredits: {
    ESSENTIAL_TIER: 800,
    PROFESSIONAL_TIER: 4000,
    personal_basic: 200,
    personal_full: 1000,
  },
}

export const DEFAULT_OWNER_SETTINGS: OwnerSettings = {
  maintenanceMode: {
    enabled: false,
    message: '',
  },
  debugSessionRetentionDays: 14,
  ownerConsoleLeaseMinutes: 30,
  ownerAlertEmail: '',
  publicCatalogFreeze: false,
  globalSignupBonusCr: 200,
  globalDailyNewSignupCrCap: 500,
  globalAiGenerationCurseDefaults: {
    enabled: false,
    durationHours: 24,
    breakCostCr: 200,
  },
  economy: DEFAULT_ECONOMY_SETTINGS,
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}

const cleanText = (value: unknown, max: number) =>
  typeof value === 'string' ? value.trim().slice(0, max) : ''

const cleanInteger = (value: unknown, fallback: number, min: number, max: number) => {
  const numeric = Math.round(Number(value))
  if (!Number.isSafeInteger(numeric)) return fallback
  return Math.min(Math.max(numeric, min), max)
}

export const normalizeMaintenanceMode = (value: unknown): OwnerSettings['maintenanceMode'] => {
  if (typeof value === 'boolean') {
    return { enabled: value, message: '' }
  }
  const record = asRecord(value)
  return {
    enabled: record.enabled === true,
    message: cleanText(record.message, 500),
  }
}

const normalizeAiGenerationCurseDefaults = (value: unknown): OwnerSettings['globalAiGenerationCurseDefaults'] => {
  const record = asRecord(value)
  return {
    enabled: record.enabled === true,
    durationHours: cleanInteger(record.durationHours, DEFAULT_OWNER_SETTINGS.globalAiGenerationCurseDefaults.durationHours, 1, 168),
    breakCostCr: cleanInteger(record.breakCostCr, DEFAULT_OWNER_SETTINGS.globalAiGenerationCurseDefaults.breakCostCr, 0, 100_000),
  }
}

const cleanFloat = (value: unknown, fallback: number, min: number, max: number): number => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return fallback
  return Math.min(Math.max(numeric, min), max)
}

const cleanSpinRange = (value: unknown, fallback: SpinTierRange): SpinTierRange => {
  const rec = asRecord(value)
  const minCredits = cleanInteger(rec.minCredits, fallback.minCredits, 0, 100_000)
  const maxCredits = cleanInteger(rec.maxCredits, fallback.maxCredits, minCredits, 100_000)
  return { minCredits, maxCredits }
}

const cleanLadder = (value: unknown, fallback: number[]): number[] => {
  if (!Array.isArray(value) || value.length === 0) return [...fallback]
  return value.map((entry, idx) => cleanInteger(entry, fallback[idx] ?? 0, 0, 100_000))
}

const cleanPlanMonthlyCredits = (value: unknown, fallback: PlanMonthlyCredits): PlanMonthlyCredits => {
  const rec = asRecord(value)
  return {
    ESSENTIAL_TIER: cleanInteger(rec.ESSENTIAL_TIER, fallback.ESSENTIAL_TIER, 0, 1_000_000),
    PROFESSIONAL_TIER: cleanInteger(rec.PROFESSIONAL_TIER, fallback.PROFESSIONAL_TIER, 0, 1_000_000),
    personal_basic: cleanInteger(rec.personal_basic, fallback.personal_basic, 0, 1_000_000),
    personal_full: cleanInteger(rec.personal_full, fallback.personal_full, 0, 1_000_000),
  }
}

export const normalizeEconomySettings = (value: unknown): EconomySettings => {
  const record = asRecord(value)
  const spinRangesRecord = asRecord(record.spinRanges)
  return {
    referredSignupBonus: cleanInteger(record.referredSignupBonus, DEFAULT_ECONOMY_SETTINGS.referredSignupBonus, 0, 100_000),
    checklistRewardCredits: cleanInteger(record.checklistRewardCredits, DEFAULT_ECONOMY_SETTINGS.checklistRewardCredits, 0, 100_000),
    checklistDailyLimit: cleanInteger(record.checklistDailyLimit, DEFAULT_ECONOMY_SETTINGS.checklistDailyLimit, 0, 100_000),
    feedbackRewardCredits: cleanInteger(record.feedbackRewardCredits, DEFAULT_ECONOMY_SETTINGS.feedbackRewardCredits, 0, 100_000),
    spinRanges: {
      lucky: cleanSpinRange(spinRangesRecord.lucky, DEFAULT_ECONOMY_SETTINGS.spinRanges.lucky),
      mega: cleanSpinRange(spinRangesRecord.mega, DEFAULT_ECONOMY_SETTINGS.spinRanges.mega),
      giga: cleanSpinRange(spinRangesRecord.giga, DEFAULT_ECONOMY_SETTINGS.spinRanges.giga),
    },
    spinsPerDay: cleanInteger(record.spinsPerDay, DEFAULT_ECONOMY_SETTINGS.spinsPerDay, 1, 100),
    ticketExpiryDays: cleanInteger(record.ticketExpiryDays, DEFAULT_ECONOMY_SETTINGS.ticketExpiryDays, 1, 365),
    milestoneRaidPool: cleanInteger(record.milestoneRaidPool, DEFAULT_ECONOMY_SETTINGS.milestoneRaidPool, 0, 100_000),
    bossCurseBreakCost: cleanInteger(record.bossCurseBreakCost, DEFAULT_ECONOMY_SETTINGS.bossCurseBreakCost, 0, 100_000),
    referralMaxPeople: cleanInteger(record.referralMaxPeople, DEFAULT_ECONOMY_SETTINGS.referralMaxPeople, 1, 100),
    referralLadder: cleanLadder(record.referralLadder, DEFAULT_ECONOMY_SETTINGS.referralLadder),
    referralPurchaseCredits: cleanInteger(record.referralPurchaseCredits, DEFAULT_ECONOMY_SETTINGS.referralPurchaseCredits, 0, 100_000),
    referralPurchaseHoldDays: cleanInteger(record.referralPurchaseHoldDays, DEFAULT_ECONOMY_SETTINGS.referralPurchaseHoldDays, 0, 365),
    flagHoldDays: cleanInteger(record.flagHoldDays, DEFAULT_ECONOMY_SETTINGS.flagHoldDays, 0, 365),
    transferCapPerMonth: cleanInteger(record.transferCapPerMonth, DEFAULT_ECONOMY_SETTINGS.transferCapPerMonth, 0, 100_000),
    transferMinMemberDays: cleanInteger(record.transferMinMemberDays, DEFAULT_ECONOMY_SETTINGS.transferMinMemberDays, 0, 365),
    inrPerCreditFree: cleanFloat(record.inrPerCreditFree, DEFAULT_ECONOMY_SETTINGS.inrPerCreditFree, 0.001, 1000),
    inrPerCreditPaid: cleanFloat(record.inrPerCreditPaid, DEFAULT_ECONOMY_SETTINGS.inrPerCreditPaid, 0.001, 1000),
    usdInr: cleanFloat(record.usdInr, DEFAULT_ECONOMY_SETTINGS.usdInr, 1, 1000),
    planMonthlyCredits: cleanPlanMonthlyCredits(record.planMonthlyCredits, DEFAULT_ECONOMY_SETTINGS.planMonthlyCredits),
  }
}

export const normalizeOwnerSettings = (value: unknown): OwnerSettings => {
  const record = asRecord(value)
  return {
    maintenanceMode: normalizeMaintenanceMode(record.maintenanceMode),
    debugSessionRetentionDays: cleanInteger(record.debugSessionRetentionDays, DEFAULT_OWNER_SETTINGS.debugSessionRetentionDays, 1, 365),
    ownerConsoleLeaseMinutes: cleanInteger(record.ownerConsoleLeaseMinutes, DEFAULT_OWNER_SETTINGS.ownerConsoleLeaseMinutes, 5, 240),
    ownerAlertEmail: cleanText(record.ownerAlertEmail, 320),
    publicCatalogFreeze: record.publicCatalogFreeze === true,
    globalSignupBonusCr: cleanInteger(record.globalSignupBonusCr, DEFAULT_OWNER_SETTINGS.globalSignupBonusCr, 0, 100_000),
    globalDailyNewSignupCrCap: cleanInteger(record.globalDailyNewSignupCrCap, DEFAULT_OWNER_SETTINGS.globalDailyNewSignupCrCap, 0, 100_000),
    globalAiGenerationCurseDefaults: normalizeAiGenerationCurseDefaults(record.globalAiGenerationCurseDefaults),
    economy: normalizeEconomySettings(record.economy),
  }
}

export const ownerSettingsToFirestore = (settings: OwnerSettings, actorUid: string) => {
  const normalized = normalizeOwnerSettings(settings)
  return {
    maintenanceMode: {
      ...normalized.maintenanceMode,
      updatedBy: actorUid,
    },
    debugSessionRetentionDays: normalized.debugSessionRetentionDays,
    ownerConsoleLeaseMinutes: normalized.ownerConsoleLeaseMinutes,
    ownerAlertEmail: normalized.ownerAlertEmail,
    publicCatalogFreeze: normalized.publicCatalogFreeze,
    globalSignupBonusCr: normalized.globalSignupBonusCr,
    globalDailyNewSignupCrCap: normalized.globalDailyNewSignupCrCap,
    globalAiGenerationCurseDefaults: normalized.globalAiGenerationCurseDefaults,
    economy: normalized.economy,
  }
}

/** R35 DL5.b: `system/config.demoFreeze` as the Owner Console reads it (written only by SET_DEMO_FREEZE on the server). */
export type OwnerDemoFreeze = {
  /** True only while `active` is set and `until` is still in the future when the snapshot was read. */
  active: boolean
  untilMillis: number | null
  note: string
  setBy: string
}

const timestampMillis = (value: unknown): number | null => {
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    const millis = (value as { toMillis: () => number }).toMillis()
    return Number.isFinite(millis) ? millis : null
  }
  const seconds = (value as { seconds?: unknown } | null)?.seconds
  return typeof seconds === 'number' && Number.isFinite(seconds) ? seconds * 1000 : null
}

export const normalizeDemoFreeze = (config: unknown, nowMs: number): OwnerDemoFreeze => {
  const freeze = asRecord(asRecord(config).demoFreeze)
  const untilMillis = timestampMillis(freeze.until)
  return {
    active: freeze.active === true && untilMillis !== null && untilMillis > nowMs,
    untilMillis,
    note: typeof freeze.note === 'string' ? freeze.note : '',
    setBy: typeof freeze.setBy === 'string' ? freeze.setBy : '',
  }
}
